import { useCallback } from 'react';
import type { SpassuCodeApi } from '../../api';
import type { AzureRepository } from '../../api/types';
import { useAsyncOptions } from './useAsyncOptions';

interface AzureOptionsContext {
  enabled: boolean;
  project: string;
  repository: string;
  isAdmin: boolean;
  rbacLoading: boolean;
  rbacError: unknown;
  hasPermission: (project: string, permission: 'activity.create') => boolean;
}

// Preserve the existing project/repository RBAC filtering and its fallback.
async function accessibleRepositories(
  api: SpassuCodeApi,
  project: string,
  filter: boolean,
  isCurrent: () => boolean,
): Promise<AzureRepository[]> {
  const repositories = await api.listRepositories(project);
  if (!isCurrent()) return [];
  if (!filter) return repositories;
  const roles = await api.getMyRoles();
  if (!isCurrent()) return [];
  const access = roles.data.find(
    item => item.projectKey === project || item.projectKey === '*',
  );
  if (!access || access.allRepositories || access.repositories.length === 0)
    return repositories;
  const allowed = new Set(access.repositories);
  return repositories.filter(item => allowed.has(item.name));
}

export function useAzureRepositoryOptions(
  api: SpassuCodeApi,
  context: AzureOptionsContext,
) {
  const {
    enabled,
    project,
    repository,
    isAdmin,
    rbacLoading,
    rbacError,
    hasPermission,
  } = context;
  const filter = !rbacLoading && !rbacError && !isAdmin;
  const projects = useAsyncOptions(
    useCallback(async () => {
      const result = await api.listProjects();
      return filter
        ? result.filter(item => hasPermission(item.name, 'activity.create'))
        : result;
    }, [api, filter, hasPermission]),
    enabled,
  );
  const repositories = useAsyncOptions(
    useCallback(
      (isCurrent: () => boolean) =>
        accessibleRepositories(api, project, filter, isCurrent),
      [api, project, filter],
    ),
    enabled && Boolean(project),
  );
  const repo = repositories.items.find(item => item.name === repository);
  const branches = useAsyncOptions(
    useCallback(
      () => api.listBranches(project, repo!.id),
      [api, project, repo],
    ),
    enabled && Boolean(repo),
  );
  return { projects, repositories, branches };
}
